import { CSSProperties, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";
import AppAccountSettingHeader from "@app/(auth-approved-patient)/app-account-setting-header/app-account-setting-header";
import styles from "@app/(auth-approved-patient)/app-account-setting/layout.module.css";
import { useKeyboardInset } from "@app/(auth-approved-patient)/hooks/use-keyboard-inset";

export default function AppAccountSettingLayout() {
  const location = useLocation();
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const keyboardInset = useKeyboardInset();
  const isParentalConsentPage = location.pathname.includes("/app-account-setting/parental-consent");
  const isSetupAccountPage = location.pathname.includes("/app-account-setting/setup-account");
  const isCheckNamePage = location.pathname.includes("/app-account-setting/check-name");
  const isCompletePage = location.pathname.includes("/app-account-setting/complete");
  const isConfirmInfoPage = location.pathname.includes("/app-account-setting/confirm-info");
  const shouldLockPageScroll =
    isParentalConsentPage || isCheckNamePage || isCompletePage || isConfirmInfoPage;
  const shouldUseInFlowHeader = isSetupAccountPage;

  useEffect(() => {
    if (!window.history.scrollRestoration) {
      return;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    const previousHtmlBg = document.documentElement.style.backgroundColor;
    const previousBodyBg = document.body.style.backgroundColor;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY;
    const previousBodyOverscroll = document.body.style.overscrollBehaviorY;

    document.documentElement.style.backgroundColor = "var(--color-warm-grey-98)";
    document.body.style.backgroundColor = "var(--color-warm-grey-98)";
    document.documentElement.style.overscrollBehaviorY = "none";
    document.body.style.overscrollBehaviorY = "none";

    return () => {
      document.documentElement.style.backgroundColor = previousHtmlBg;
      document.body.style.backgroundColor = previousBodyBg;
      document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll;
      document.body.style.overscrollBehaviorY = previousBodyOverscroll;
    };
  }, []);

  // iOS Safari 대응: 스크롤 잠금 페이지에서는 html/body를 fixed로 잠궈 브라우저 레벨 스크롤을 차단
  useEffect(() => {
    if (!shouldLockPageScroll) {
      return;
    }

    // 이전 화면(계정 설정 등)에서 키보드로 창이 스크롤된 상태로 잠그면 body 가 그만큼 위로 밀려
    // 상단 콘텐츠(완료 화면 체크 표시 등)가 고정 헤더에 가려지므로, 최상단으로 되돌린 뒤 잠근다
    window.scrollTo(0, 0);
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;
    const previousBodyWidth = document.body.style.width;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = "0";
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      document.body.style.width = previousBodyWidth;
    };
  }, [shouldLockPageScroll]);

  // 페이지 전환 시 스크롤을 최상단으로 강제 초기화
  useLayoutEffect(() => {
    const resetScroll = () => {
      if (!pageContainerRef.current) {
        return;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        activeElement.blur();
      }

      pageContainerRef.current.scrollTop = 0;
      pageContainerRef.current.scrollLeft = 0;
      pageContainerRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    resetScroll();
    const frameId = requestAnimationFrame(resetScroll);
    const timeoutId = window.setTimeout(resetScroll, 80);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  // 안전망: 레이아웃 effect 단계에서도 한 번 더 보정
  useEffect(() => {
    if (pageContainerRef.current) {
      pageContainerRef.current.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname]);

  return (
    <div className={styles.rootContainer}>
      <AppAccountSettingHeader isInFlow={shouldUseInFlowHeader} />
      <div
        ref={pageContainerRef}
        className={`${styles.pageContainer} ${shouldLockPageScroll ? styles.pageContainerNoScroll : ""} ${shouldUseInFlowHeader ? styles.pageContainerWithInFlowHeader : ""}`}
        style={{ "--keyboard-inset": `${keyboardInset}px` } as CSSProperties}
      >
        <Outlet />
      </div>
    </div>
  );
}
